import { v4 } from "uuid";

import { nextPinLayout } from "src/analytics/dashboard/dashboardLayout";
import { useJustPinned } from "src/analytics/dashboard/JustPinnedContext";
import {
  useDashboardQuery,
  useUpdateDashboard,
} from "src/api/analytics-dashboard/dashboard";
import type {
  ChartDefinition,
  Pin,
  PinLayout,
  Realm,
} from "src/clients/analytics-dashboard";

// Backend pin cap, mirrored so the UI can disable pinning before a PATCH.
export const MAX_PINS = 20;

export type Dashboard = { pins: Record<string, Pin> };

// Facade over the analytics-dashboard API for a given realm: exposes the
// dashboard's pins plus add/remove actions. Each action sends the full pins map
// (the backend PATCH is a full replace) guarded by the current version.
export const useDashboard = (
  realm: Realm,
  realmId: string,
  dashboardId?: string,
  options?: { enabled?: boolean },
): {
  dashboard: Dashboard;
  isLoading: boolean;
  hasLoadFailed: boolean;
  pinChart: (definition: ChartDefinition, id?: string, title?: string) => void;
  unpinChart: (id: string) => void;
  updatePinTitle: (id: string, title: string) => void;
  updatePinLayouts: (layouts: Record<string, PinLayout>) => Promise<void>;
  isUpdating: boolean;
  isAtPinCap: boolean;
} => {
  const { data, isPending, isError } = useDashboardQuery(
    realm,
    realmId,
    dashboardId,
    options,
  );
  const {
    commit,
    commitAsync,
    isPending: isUpdating,
  } = useUpdateDashboard(realm, realmId, dashboardId);
  const { markPinned } = useJustPinned();

  const pins: Record<string, Pin> = data?.pins ?? {};

  const pinChart = (
    definition: ChartDefinition,
    id?: string,
    title?: string,
  ): void => {
    if (!data) return;
    const pinId = id ?? v4();
    if (pinId in pins) return;
    if (Object.keys(pins).length >= MAX_PINS) return;
    const pin: Pin = {
      id: pinId,
      title: title ?? null,
      layout: nextPinLayout(Object.values(pins)),
      definition,
    };
    commit(
      (current) => ({
        ...current,
        pins: { ...current.pins, [pinId]: pin },
      }),
      "Couldn't pin chart to dashboard",
    );
    markPinned(pinId);
  };

  const unpinChart = (id: string): void => {
    if (!data || !(id in pins)) return;
    commit(
      (current) => ({
        ...current,
        pins: Object.fromEntries(
          Object.entries(current.pins).filter(([pinId]) => pinId !== id),
        ),
      }),
      "Couldn't remove chart from dashboard",
    );
  };

  const updatePinTitle = (id: string, title: string): void => {
    if (!data || !(id in pins)) return;
    commit(
      (current) => ({
        ...current,
        pins: {
          ...current.pins,
          [id]: { ...current.pins[id], title },
        },
      }),
      "Couldn't rename chart",
    );
  };

  const updatePinLayouts = async (
    layouts: Record<string, PinLayout>,
  ): Promise<void> => {
    if (!data) return;
    await commitAsync((current) => ({
      ...current,
      pins: Object.fromEntries(
        Object.entries(current.pins).map(([id, pin]) => [
          id,
          layouts[id] ? { ...pin, layout: layouts[id] } : pin,
        ]),
      ),
    }));
  };

  return {
    dashboard: { pins },
    isLoading: isPending,
    // A failed refetch keeps the last good data, which is still usable.
    hasLoadFailed: isError && data === undefined,
    pinChart,
    unpinChart,
    updatePinTitle,
    updatePinLayouts,
    isUpdating,
    isAtPinCap: Object.keys(pins).length >= MAX_PINS,
  };
};
